import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { BookmarkIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { createInformationRequest } from '../../api/information-requests/create-information-request'
import UserNavBar from '../../components/elements/nav-bar'
import InformationRequestForm from '../../components/pages/information-request/information-request-form'
import { Paths } from '../../lib/constants/path'
import useLoading from '../../lib/hooks/use-loading'
import { EmailValidityStatusEnum } from '../../lib/types/enums/information-requests/email-validity-status.enum'
import { OrigineEnum } from '../../lib/types/enums/information-requests/origin.enum'
import { ReceiveBrochureEnum } from '../../lib/types/enums/information-requests/recieve-brochure.enum'
import { twoButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { checkForFrance, showAxiosError } from '../../lib/utils/functions/misc.functions'

const InformationRequestCreatePage = () => {
  const { navigate, back } = useNavigator()
  const queryClient = useQueryClient()
  const { startLoading, stopLoading } = useLoading()

  const navigateToUpdate = (id: number) =>
    navigate(Paths.INFORMATION_REQUEST_UPDATE_PAGE.replace(':id', id.toString()), {
      replace: true
    })

  const { mutateAsync } = useMutation({
    mutationFn: createInformationRequest,
    onError: showAxiosError,
    onSuccess: (informationRequest) => {
      queryClient.invalidateQueries({ queryKey: ['information-requests'] })

      if (informationRequest.emailValidityStatus === EmailValidityStatusEnum.UNKNOWN) {
        startLoading("Vérifier la validation de l'e-mail")
        // Invalidate the pcr query to refetch the updated data
        setTimeout(() => {
          stopLoading()
          // I am adding the display toast and navigate to the if and else confdition since outside they don't run in order
          // example I would navigate to the page before the end of the validation of the email is done
          toast.success('Demande de brochure créée avec succès')

          navigateToUpdate(informationRequest.id)
        }, 8000)
      } else {
        toast.success('Demande de brochure créée avec succès')
        navigateToUpdate(informationRequest.id)
      }
    }
  })

  return (
    <>
      <UserNavBar title="Demande de brochure" icon={BookmarkIcon} />
      <div className="mb-4" />
      <Box className="p-5 flex flex-col gap-4">
        <Typography variant="sub-heading" color={tailwindColors.grey.DEFAULT}>
          Veuillez remplir le formulaire ci-dessous
        </Typography>

        <InformationRequestForm
          defaultValues={{
            origin: OrigineEnum.PHONE,
            receiveBrochureBy: ReceiveBrochureEnum.EMAIL_AND_COURIER
          }}
          buttonsConfig={twoButtonsConfig({
            onCancel: () => back(),
            onConfirm: async (values) => {
              await mutateAsync({
                ...values,
                ...(values.country && { inFrance: checkForFrance(values.country) })
              })
            }
          })}
        />
      </Box>
    </>
  )
}

export default InformationRequestCreatePage
